<script>
// Vuex
import { useStore } from 'dashboard/composables/store';
import { mapGetters } from 'vuex';

// Vue Router
import { useRouter } from 'vue-router';

// Composables
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useUISettings } from 'dashboard/composables/useUISettings';

// Helpers
import { setColorTheme } from 'dashboard/helper/themeHelper';
import { isOnboardingView } from 'v3/helpers/RouteHelper';

// App Components
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import LoadingState from './components/widgets/LoadingState.vue';

// Services & Actions
import ReconnectService from 'dashboard/helper/ReconnectService';
import vueActionCable from './helper/actionCable';
import { registerSubscription, verifyServiceWorkerExistence } from './helper/pushHelper';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
      operatorSessionInactive: false,
      operatorSessionBusy: false,
      operatorSessionError: '',
      operatorSessionTimer: null,
      operatorSessionsAvailable: null,
      operatorSessionCopy: {
        title: 'Ruut is active on another device',
        description:
          'Your work here is saved. Resume on this device when you are ready to switch back.',
        resuming: 'Resuming…',
        resume: 'Resume on this device',
      },
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    window.addEventListener('ruut:operator-session-inactive', this.onOperatorSessionInactive);
    window.addEventListener('focus', this.checkOperatorSession);
    this.checkOperatorSession();
    this.operatorSessionTimer = window.setInterval(this.checkOperatorSession, 30000);
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(this.uiSettings?.locale || window.chatwootConfig.selectedLocale);
  },
  unmounted() {
    window.removeEventListener('ruut:operator-session-inactive', this.onOperatorSessionInactive);
    window.removeEventListener('focus', this.checkOperatorSession);
    window.clearInterval(this.operatorSessionTimer);
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    onOperatorSessionInactive() {
      this.operatorSessionsAvailable = true;
      this.operatorSessionInactive = true;
    },
    async checkOperatorSession() {
      if (!this.currentUser?.id || this.operatorSessionsAvailable === false) return;
      try {
        const { data } = await window.axios.get('/api/v1/operator_sessions');
        this.operatorSessionsAvailable = true;
        const current = data.sessions.find(session => session.current);
        this.operatorSessionInactive = Boolean(current && !current.active);
      } catch (error) {
        if (error?.response?.status === 404) this.operatorSessionsAvailable = false;
      }
    },
    async resumeOperatorSession() {
      this.operatorSessionBusy = true;
      this.operatorSessionError = '';
      try {
        await window.axios.post('/api/v1/operator_sessions/takeover');
        window.location.reload();
      } catch (_) {
        this.operatorSessionError = 'Could not resume this session. Please sign in again.';
        this.operatorSessionBusy = false;
      }
    },
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      try {
        await this.$store.dispatch('accounts/get');
      } catch (_) {
        await this.checkOperatorSession();
        return;
      }
      const account = this.getAccount(this.currentAccountId);
      if (!account) {
        await this.checkOperatorSession();
        return;
      }
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } = account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      let operatorSessionTicket = null;
      try {
        const { data } = await window.axios.get('/api/v1/operator_sessions');
        this.operatorSessionsAvailable = true;
        operatorSessionTicket = data.cable_ticket;
      } catch (error) {
        if (error?.response?.status === 404) this.operatorSessionsAvailable = false;
      }
      vueActionCable.init(this.store, pubsubToken, operatorSessionTicket);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
    <div v-if="operatorSessionInactive" class="operator-session-overlay">
      <div
        class="operator-session-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="operator-session-title"
      >
        <h2 id="operator-session-title">{{ operatorSessionCopy.title }}</h2>
        <p>{{ operatorSessionCopy.description }}</p>
        <p v-if="operatorSessionError" role="alert">{{ operatorSessionError }}</p>
        <button type="button" :disabled="operatorSessionBusy" @click="resumeOperatorSession">
          {{ operatorSessionBusy ? operatorSessionCopy.resuming : operatorSessionCopy.resume }}
        </button>
      </div>
    </div>
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.operator-session-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.65);
}
.operator-session-dialog {
  width: min(420px, calc(100vw - 32px));
  padding: 28px;
  border-radius: 16px;
  background: var(--white, #fff);
  color: var(--black, #111);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
}
.operator-session-dialog h2 {
  font-size: 20px;
  font-weight: 600;
}
.operator-session-dialog p {
  margin: 12px 0;
}
.operator-session-dialog button {
  padding: 10px 16px;
  border-radius: 8px;
  background: #9146d5;
  color: white;
}

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

.multiselect__input {
  margin-bottom: 0px !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
